import { createFileRoute, Link, notFound } from '@tanstack/react-router';
import { ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/features/auth/useAuth';
import { DioceseKpis } from '@/features/dashboard/DioceseKpis';
import { DioceseRankingPanel } from '@/features/dashboard/DioceseRankingPanel';
import { DioceseTrendPanel } from '@/features/dashboard/DioceseTrendPanel';
import {
  DIOCESE_OFFICIAL_NAMES,
  DIOCESE_STRUCTURE,
  dioceseCodeFromSlug,
  matchDioceseCode,
} from '@/features/dashboard/dioceses';
import {
  useAvailableYears,
  useMyDeaneriesPercent,
  useMyParishesPercent,
} from '@/features/dashboard/hooks';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Spinner } from '@/shared/ui/Spinner';
import { YearSelect } from '@/shared/ui/YearSelect';

export const Route = createFileRoute('/_app/diecezja/$slug')({
  loader: ({ params }) => {
    const dioceseCode = dioceseCodeFromSlug(params.slug);
    if (!dioceseCode) throw notFound();
    return { dioceseCode };
  },
  component: DioceseRoute,
});

function DioceseRoute() {
  return <DioceseContent />;
}

function DioceseContent() {
  const { t } = useTranslation();
  const { dioceseCode } = Route.useLoaderData();
  const structure = DIOCESE_STRUCTURE[dioceseCode];
  const name = DIOCESE_OFFICIAL_NAMES[dioceseCode];

  const yearsQuery = useAvailableYears();
  const years = yearsQuery.data ?? [];
  const latestYear = years.length > 0 ? Math.max(...years) : undefined;

  const [pickedYear, setPickedYear] = useState<number | undefined>(undefined);
  const effectiveYear = pickedYear ?? latestYear;

  // Liczby dekanatów/parafii: backend nie wystawia ich publicznie, więc dla
  // anonimowych i obcych diecezji zostaje statyczny schematyzm
  // (`DIOCESE_STRUCTURE`). Gdy jednak zalogowana diecezja ogląda SWÓJ
  // profil, liczymy je z żywych danych za wybrany rok (te same zapytania co
  // „Moje dekanaty/parafie” na `/`, react-query je współdzieli) — schematyzm
  // potrafi się rozjechać z importem (np. tarnowska: 459 vs 466 parafii).
  const { session } = useAuth();
  const isOwnDiocese =
    session?.scope.kind === 'diocese' &&
    matchDioceseCode(session.scope.name) === dioceseCode;
  const liveDeaneries = useMyDeaneriesPercent(effectiveYear, isOwnDiocese);
  const liveParishes = useMyParishesPercent(effectiveYear, isOwnDiocese);
  const deaneriesCount =
    isOwnDiocese && liveDeaneries.data
      ? liveDeaneries.data.length
      : structure.deaneries;
  const parishesCount =
    isOwnDiocese && liveParishes.data ? liveParishes.data.length : structure.parishes;

  return (
    <>
      <nav className="text-muted mb-4 flex items-center gap-1.5 text-[13px]">
        <Link to="/" className="hover:text-ink font-medium">
          {t('dashboard.diocese.breadcrumbHome')}
        </Link>
        <ChevronRight className="size-3.5" aria-hidden="true" />
        <span className="text-ink" aria-current="page">
          {name}
        </span>
      </nav>

      <PageHeader
        eyebrow={t('dashboard.diocese.eyebrow')}
        title={name}
        description={[
          t('dashboard.diocese.deaneriesCount', { count: deaneriesCount }),
          t('dashboard.diocese.parishesCount', { count: parishesCount }),
        ].join(' · ')}
        actions={
          effectiveYear !== undefined && (
            <YearSelect
              years={[...years].sort((a, b) => b - a)}
              value={effectiveYear}
              onChange={setPickedYear}
              label={t('dashboard.diocese.yearSelectLabel')}
            />
          )
        }
      />

      {yearsQuery.isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner label={t('common.loading')} />
        </div>
      ) : effectiveYear === undefined ? (
        <p className="text-muted text-sm">{t('dashboard.noYears')}</p>
      ) : (
        <div className="flex flex-col gap-6">
          <DioceseKpis dioceseCode={dioceseCode} year={effectiveYear} />

          <div className="grid gap-6 lg:grid-cols-2">
            <DioceseTrendPanel dioceseCode={dioceseCode} availableYears={years} />
            <DioceseRankingPanel dioceseCode={dioceseCode} year={effectiveYear} />
          </div>
        </div>
      )}
    </>
  );
}
